import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { isEmpty, groupBy, capitalize } from "lodash";
import { useQuery } from "@apollo/client";
import { Select } from "orcs-design-system";
import { getObjectiveTypes } from "src/queries/objectives.graphql";

const buildGroupedOptions = (types) => {
  const groupedByDatasource = groupBy(types, "datasource");
  return Object.entries(groupedByDatasource).map(([datasource, dsTypes]) => ({
    label: capitalize(datasource),
    options: dsTypes.map((type) => ({
      label: capitalize(type.type),
      value: type.type,
    })),
  }));
};

const ObjectiveTypeSelector = ({
  value = [],
  onChange,
  label,
  placeholder,
  inputId = "objective-type-selector",
  isLoading: externalLoading = false,
  ...restProps
}) => {
  const [objectiveTypes, setObjectiveTypes] = useState([]);

  const { loading: queryLoading } = useQuery(getObjectiveTypes, {
    onCompleted: (data) => {
      if (data?.getObjectiveTypes) {
        setObjectiveTypes(buildGroupedOptions(data.getObjectiveTypes));
      }
    },
  });

  const selectedTypes = useMemo(
    () =>
      (value || []).map((type) => ({ label: capitalize(type), value: type })),
    [value]
  );

  const handleChange = useCallback(
    (selected) => {
      const newValues = isEmpty(selected)
        ? []
        : selected.map((opt) => opt.value);
      onChange(newValues);
    },
    [onChange]
  );

  return (
    <Select
      isMulti
      isLoading={queryLoading || externalLoading}
      options={objectiveTypes}
      value={selectedTypes}
      onChange={handleChange}
      label={label}
      placeholder={placeholder}
      inputId={inputId}
      fullWidth
      {...restProps}
    />
  );
};

ObjectiveTypeSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  inputId: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default ObjectiveTypeSelector;
